import classnames from "classnames";
import { useSelector } from "react-redux";

import { getDomainCountryCode } from "redux/selectors/config";

import LegalLogo from "components/Icons/legal-logo.svg";
import IconSvg from "components/IconSvg";

import Accordion from "../../Accordion";
import styles from "./Footer.module.css";

// need to make sure these can differentiate between .com and .ca
const baseUrl = (isCA: boolean) => `https://www.leafly.c${isCA ? "a" : "om"}`;
const successUrl = (isCA: boolean) =>
  `https://success.leafly.c${isCA ? "a" : "om"}`;

const socialLinks = [
  {
    icon: "facebook",
    title: "Leafly on Facebook",
    url: "http://www.facebook.com/leaflydotcom",
  },
  {
    icon: "twitter",
    title: "Leafly on Twitter",
    url: "http://twitter.com/leafly",
  },
  {
    icon: "instagram",
    title: "Leafly on Instagram",
    url: "https://www.instagram.com/heyleafly",
  },
  {
    icon: "youtube",
    title: "Leafly on Youtube",
    url: "https://www.youtube.com/channel/UCqVnkns6Gf3W7J9zWZE4EqA",
  },
];

const about = (isCA: boolean) => ({
  id: "about",
  links: [
    { title: "About us", url: `${baseUrl(isCA)}/info/about` },
    { title: "Careers", url: `${baseUrl(isCA)}/info/jobs` },
    { title: "Newsroom", url: `${baseUrl(isCA)}/newsroom` },
    { title: "Investor relations", url: `${successUrl(isCA)}/invest` },
    { title: "Contact us", url: `${baseUrl(isCA)}/info/contact` },
    { title: "FAQs", url: `https://help.leafly.com` },
    { title: "Accessibility", url: `${baseUrl(isCA)}/info/accessibility` },
  ],
  title: "About Leafly",
});

const business = (isCA: boolean) => ({
  id: "business",
  links: isCA
    ? [
        {
          title: "List your store",
          url: `https://success.leafly.ca?utm_source=dispensary_link&utm_medium=footer&utm_campaign=sell_on_leafly&utm_content=SOL`,
        },
        {
          title: "List your brand",
          url: `https://success.leafly.ca?utm_source=brands_link&utm_medium=footer&utm_campaign=sell_on_leafly&utm_content=SOL`,
        },
        { title: "Lab partners", url: `https://success.leafly.ca/labs` },
      ]
    : [
        {
          title: "List your store",
          url: `${successUrl(
            false
          )}/retail?utm_source=site_footer&utm_medium=footer&utm_campaign=sell_on_leafly&utm_content=list_your_store`,
        },
        {
          title: "List your CBD store",
          url: `${successUrl(
            false
          )}/cbd?utm_source=site_footer&utm_medium=footer&utm_campaign=sell_on_leafly&utm_content=list_your_cbd_store`,
        },
        {
          title: "List your brand",
          url: `${successUrl(
            false
          )}/brands?utm_source=site_footer&utm_medium=footer&utm_campaign=sell_on_leafly&utm_content=list_your_brand`,
        },
        {
          title: "List your practice",
          url: `${successUrl(
            false
          )}/doctors?utm_source=site_footer&utm_medium=footer&utm_campaign=sell_on_leafly&utm_content=list_your_practice`,
        },
        {
          title: "Certify your lab",
          url: `${successUrl(
            false
          )}/labs?utm_source=site_footer&utm_medium=footer&utm_campaign=sell_on_leafly&utm_content=certify_your_lab`,
        },
        {
          title: "Business log in",
          url: `https://business.leafly.com`,
        },
      ],
  title: "Business Solutions",
});

const dispensaries = (isCA: boolean) => ({
  id: "dispensaries",
  links: isCA
    ? [
        {
          title: "British Columbia",
          url: `${baseUrl(true)}/dispensaries/british-columbia`,
        },
        { title: "Ontario", url: `${baseUrl(true)}/dispensaries/ontario` },
        { title: "Alberta", url: `${baseUrl(true)}/dispensaries/alberta` },
        {
          title: "Saskatchewan",
          url: `${baseUrl(true)}/dispensaries/saskatchewan`,
        },
        { title: "Manitoba", url: `${baseUrl(true)}/dispensaries/manitoba` },
        {
          title: "Prince Edward Island",
          url: `${baseUrl(true)}/dispensaries/prince-edward-island`,
        },
      ]
    : [
        {
          title: "Los Angeles",
          url: `${baseUrl(false)}/dispensaries/california/los-angeles`,
        },
        {
          title: "Seattle",
          url: `${baseUrl(false)}/dispensaries/washington/seattle`,
        },
        {
          title: "Portland",
          url: `${baseUrl(false)}/dispensaries/oregon/portland`,
        },
        {
          title: "San Francisco",
          url: `${baseUrl(false)}/dispensaries/california/san-francisco`,
        },
        {
          title: "Toronto",
          url: `${baseUrl(true)}/dispensaries/ontario/toronto`,
        },
        {
          title: "Detroit",
          url: `${baseUrl(false)}/dispensaries/michigan/detroit`,
        },
      ],
  title: isCA ? "Stores in" : "Dispensaries in",
});

const privacy = (isCA: boolean) => ({
  id: "privacy",
  links: [
    { title: "Terms of use", url: `${baseUrl(isCA)}/info/terms-of-use` },
    {
      title: "Commercial terms of use",
      url: `${baseUrl(isCA)}/info/commercial-terms-of-use`,
    },
    { title: "Privacy policy", url: `${baseUrl(isCA)}/info/privacy-policy` },
    {
      title: "Do not sell my personal information",
      url: `${baseUrl(isCA)}/info/privacy-policy#ccpa-do-not-sell`,
    },
  ],
  title: "Privacy & Terms",
});

function Footer() {
  const countryCode = useSelector(getDomainCountryCode);
  const isCA = countryCode === "CA";

  return (
    <footer className="bg-leafly-white border-t border-light-grey flex flex-col">
      <div className="container flex flex-row  justify-center">
        <a className="text-green my-md" aria-label="go to leafly.com" href="/">
          <LegalLogo width="84" height="33" />
        </a>
      </div>
      <hr className="border-light-grey" />
      <div className="flex lg:flex-row lg:py-xl py-md justify-center">
        {socialLinks.map((link) => (
          <a
            key={link.url}
            href={link.url}
            aria-label={`go to ${link.url}`}
            className="block mx-md"
            title={link.title}
          >
            <IconSvg
              width="24"
              height="24"
              filePath={`share/${link.icon}.svg`}
            />
          </a>
        ))}
      </div>
      <hr className="border-light-grey" />
      <Accordion {...about(isCA)} />
      <hr className="border-light-grey" />
      <Accordion {...business(isCA)} />
      <hr className="border-light-grey" />
      <Accordion {...dispensaries(isCA)} />
      <hr className="border-light-grey" />
      <Accordion {...privacy(isCA)} />
      <hr className="border-light-grey" />
      <div className="flex justify-between py-md px-lg">
        <a
          className="mr-md"
          href="https://apps.apple.com/app/apple-store/id416456429?pt=452354&ct=Leafly%20Footer&mt=8"
          title="Leafly on the App Store"
        >
          <img
            className=" lazyload mb-md footer__download"
            height="40"
            width="119"
            alt="Download Leafly: Marijuana Reviews on the App Store"
            data-src={`https://public.leafly.com/cephalopod/images/app-store.svg`}
          />
        </a>
        <a
          href="https://play.google.com/store/apps/details?id=leafly.android&referrer=utm_source%3Dleafly%26utm_medium%3Dreferral%26utm_campaign%3Dfooter"
          title="Leafly on Google Play"
        >
          <img
            className="lazyload mb-md"
            height="40"
            width="134"
            alt="Download Leafly Marijuana Reviews on Google Play"
            data-src={`https://public.leafly.com/cephalopod/images/google-play.svg`}
          />
        </a>
      </div>
      <hr className="border-light-grey" />
      <section
        className={classnames(
          styles.footerCopyright,
          "footer__copyright border-b border-light-grey container"
        )}
      >
        <div className="my-xl text-xs flex flex-col items-center text-center">
          <div>&copy; {new Date().getFullYear()} Leafly Holdings, Inc.</div>
          <div>
            Leafly and the Leafly logo are registered trademarks of Leafly
            Holdings, Inc. All Rights Reserved.
          </div>
        </div>
      </section>
    </footer>
  );
}

export default Footer;
